import React, { useCallback, useEffect, useState } from 'react';

import {
  PDFCheckBox,
  PDFDocument,
  PDFField,
  PDFRadioGroup,
  PDFTextField,
} from 'pdf-lib';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Table,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  SelectChangeEvent,
  Menu,
  Accordion,
  AccordionSummary,
  CircularProgress,
  Grid2,
} from '@mui/material';
import {
  ArrowBigDown,
  ArrowBigUp,
  MoreVertical,
  Star,
  Trash2,
} from 'lucide-react';
import {
  CheckBoxes,
  CheckBoxesAnswer,
  Questionnaire,
  QuestionnaireQuestion,
  TextQuestion,
  RadioQuestion,
  RadioQuestionAnswer,
  TextQuestionAnswer,
  DateQuestion,
} from '../../../types/questionnaire.types';
import { questionnaireService } from '../../../services/questionnaire.service';

interface FormEditorDialogProps {
  open: boolean;
  questionnaireID: string;
  selectedQuestionnaire: Questionnaire;
  onClose: () => void;
}

const FormEditorDialog: React.FC<FormEditorDialogProps> = ({
  open,
  questionnaireID,
  selectedQuestionnaire,
  onClose,
}) => {
  const defaultQuestionnaire = useCallback(() => {
    return {
      id: '-1',
      title: 'you should not be able to see this',
      description: '',
      status: 'Draft',
      regionID: '',
      fairID: '',
      questions: [],
      pdfURL: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    } as Questionnaire;
  }, []);

  const [questionnaire, setQuestionnaire] = useState<Questionnaire>(
    defaultQuestionnaire()
  );

  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setUploading] = useState(false);
  const [pdfBlobURL, setpdfBlobURL] = useState<string>(
    'https://firebasestorage.googleapis.com/v0/b/fairtechweb-dd440.appspot.com/o/questionnaires%2Fassets%2Fdefault.pdf?alt=media&token=bea8c8e7-8b5f-47ec-a53f-63874f4f5ded'
  );
  const [newFileUploaded, setNewFileUploaded] = useState<boolean>(false);
  const [fieldNames, setFieldNames] = useState<PDFField[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [newDescription, setNewDescription] = useState('');

  const [saveError, setSaveError] = useState(false);
  const [saveErrorText, setSaveErrorText] = useState('');

  //Retrieves the names of the fields from a pdf url
  const getFieldsFromURL = useCallback(async (url: string) => {
    try {
      var blob = await questionnaireService.retrievePDF(url);
      if (blob) {
        const pdfDoc = PDFDocument.load(await blob.arrayBuffer());
        const form = (await pdfDoc).getForm();
        const fields = form.getFields();
        setFieldNames(fields);
        return fields;
      }
    } catch (err) {}
  }, []);

  //Retrieves the names of the fields from a pdf Blob
  const getFieldsFromBlob = async (blob: Blob) => {
    try {
      const pdfDoc = PDFDocument.load(await blob.arrayBuffer());
      const form = (await pdfDoc).getForm();
      const fields = form.getFields();
      setFieldNames(fields);
      return fields;
    } catch (err) {}
  };

  const loadPDF = useCallback(
    async (url: string) => {
      if (url !== '') {
        const blob = await questionnaireService.retrievePDF(url);
        if (blob instanceof Blob) {
          var blobURL = URL.createObjectURL(blob);
          setpdfBlobURL(blobURL);
          getFieldsFromURL(blobURL);
        }
      }
    },
    [getFieldsFromURL]
  );

  const loadQuestionnaire = useCallback(async () => {
    setLoading(true);
    setQuestionnaire(selectedQuestionnaire);
    setNewDescription(selectedQuestionnaire.description);
    await loadPDF(selectedQuestionnaire.pdfURL);
    await getFieldsFromURL(selectedQuestionnaire.pdfURL);
    setLoading(false);
  }, [selectedQuestionnaire, loadPDF, getFieldsFromURL]);

  const reset = useCallback(() => {
    setpdfBlobURL(
      'https://firebasestorage.googleapis.com/v0/b/fairtechweb-dd440.appspot.com/o/questionnaires%2Fassets%2Fdefault.pdf?alt=media&token=bea8c8e7-8b5f-47ec-a53f-63874f4f5ded'
    );
    setQuestionnaire(defaultQuestionnaire);
    setError(null);
    setSelectedFile(null);
    setUploading(false);
    setNewFileUploaded(false);
    setFieldNames([]);
    setLoading(false);
  }, [defaultQuestionnaire]);

  // When the dialog opens, load the questionnaire
  // and when it closes, reset the state
  useEffect(() => {
    if (open) {
      loadQuestionnaire();
    } else {
      reset();
    }
  }, [open, loadQuestionnaire, reset]);

  const editQuestionnaire = (edit: Partial<Questionnaire>) => {
    const newQuestionnaire = { ...questionnaire, ...edit };
    setQuestionnaire(newQuestionnaire);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      const blob = new Blob([event.target.files[0]], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      setpdfBlobURL(url);
      getFieldsFromBlob(blob);
      setNewFileUploaded(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);
    } catch (err) {
      setError('Failed to upload form. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  //Pushes a new question to the questionnaire with a unique ID
  const createQuestion = () => {
    const newQuestions = [...questionnaire.questions];

    const generateID = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      return Array.from(Array(5))
        .map(() => chars.charAt(Math.floor(Math.random() * chars.length)))
        .join('');
    };

    const isCodeUnique = async (id: string) => {
      if (questionnaire.questions.filter((obj) => obj.id === id).length === 0) {
        return true;
      } else {
        return false;
      }
    };

    let questionID;
    do {
      questionID = generateID();
    } while (!isCodeUnique(questionID));

    newQuestions.push({
      type: 'text',
      index: questionnaire.questions.length,
      id: questionID,
      questionText: '',
      questionData: {
        answers: [
          {
            label: '',
            defaultValue: '',
            pdfMap: '',
          } as TextQuestionAnswer,
        ],
      } as TextQuestion,
    } as QuestionnaireQuestion);
    editQuestionnaire({ questions: newQuestions } as Partial<Questionnaire>);
  };

  const editQuestion = (
    index: number,
    edit: Partial<QuestionnaireQuestion>
  ) => {
    const newQuestions = [...questionnaire.questions];
    newQuestions[index] = { ...newQuestions[index], ...edit };
    editQuestionnaire({ questions: newQuestions } as Partial<Questionnaire>);
  };

  const setTitle = (text: string) => {
    editQuestionnaire({ title: text } as Partial<Questionnaire>);
  };

  const isQuestionnaireValid = () => {
    setSaveError(false);
    var error = false;

    // Checks if the questionnaire title is empty string
    if (questionnaire.title === '') {
      setSaveError(true);
      setSaveErrorText('Title not valid');
      error = true;
    }
    // Checks if the questionnaire description is empty string
    if (questionnaire.description === '') {
      setSaveError(true);
      setSaveErrorText('Description not valid');
      error = true;
    }

    // Checks if the questionnaire questions are valid
    questionnaire.questions.forEach((question, questionIndex) => {
      switch (question.type) {
        case 'text':
          // Check if the question's pdfMaps are not an empty string
          (question.questionData as TextQuestion).answers.forEach(
            (answer, answerIndex) => {
              if (answer.pdfMap === '') {
                setSaveError(true);
                setSaveErrorText(
                  `Question ${questionIndex + 1}, Field ${answerIndex + 1}: Text Field PDFMap not valid`
                );
                error = true;
              }
            }
          );
          break;
        case 'radiogroup':
          // Check if the question's pdfRadioGroup is not an empty string
          if ((question.questionData as RadioQuestion).pdfRadioGroup === '') {
            setSaveError(true);
            setSaveErrorText(
              `Question ${questionIndex + 1}: PDFRadioGroup not valid`
            );
            error = true;
          }
          // Check if the question's pdfRadioMaps are not an empty string
          (question.questionData as RadioQuestion).answers.forEach(
            (answer, answerIndex) => {
              if (answer.pdfRadioMap === '') {
                setSaveError(true);
                setSaveErrorText(
                  `Question ${questionIndex + 1}, Field ${answerIndex + 1}: PDFRadioMap not valid`
                );
                error = true;
              }
            }
          );
          break;
        case 'checkboxes':
          // Check if the question's pdfMaps are not an empty string
          (question.questionData as CheckBoxes).answers.forEach(
            (answer, answerIndex) => {
              if (answer.pdfMap === '') {
                setSaveError(true);
                setSaveErrorText(
                  `Question ${questionIndex + 1}, Field ${answerIndex + 1}: Checkbox PDFMap not valid`
                );
                error = true;
              }
            }
          );
          break;
        case 'date':
          // Check if the question's pdfMap are not an empty string
          if ((question.questionData as DateQuestion).pdfMap === '') {
            setSaveError(true);
            setSaveErrorText(
              `Question ${questionIndex + 1}: Date PDFMap not valid`
            );
            error = true;
          }
          break;
      }
    });
    if (!error) {
      setSaveErrorText('');
    }
    return !error;
  };

  const saveQuestionnaire = async () => {
    setSaving(true);
    var valid = isQuestionnaireValid();
    if (valid) {
      try {
        var newQuestionnaire = {
          ...questionnaire,
        } as Questionnaire;
        if (newFileUploaded && selectedFile) {
          var url = await questionnaireService.uploadQuestionnairePDF(
            questionnaireID,
            selectedFile
          );
          newQuestionnaire = {
            ...newQuestionnaire,
            pdfURL: url,
          } as Questionnaire;
          setNewFileUploaded(false);
        }

        questionnaireService.updateQuestionnaire(
          questionnaireID,
          newQuestionnaire
        );
      } catch (error) {
        console.error('Error saving form:', error);
        throw error;
      }
    }
    setSaving(false);
  };

  const deleteQuestion = (index: number) => {
    var newQuestions = [...questionnaire.questions];

    newQuestions.splice(index, 1);
    newQuestions.map((question, index) => (question.index = index));

    const edits = {
      questions: newQuestions,
    } as Partial<Questionnaire>;

    editQuestionnaire(edits);
  };

  const swapQuestions = (index1: number, index2: number) => {
    if (
      index1 >= 0 &&
      index2 >= 0 &&
      index1 < questionnaire.questions.length &&
      index2 < questionnaire.questions.length
    ) {
      var newQuestions = [...questionnaire.questions];
      var question1 = newQuestions[index1];
      var question2 = newQuestions[index2];

      question1.index = index2;
      question2.index = index1;

      newQuestions[index1] = question2;
      newQuestions[index2] = question1;

      const edits = {
        questions: newQuestions,
      } as Partial<Questionnaire>;

      editQuestionnaire(edits);
    }
  };

  const descriptionDialogClose = (newDescription?: string) => {
    setDescriptionDialogOpen(false);
    if (newDescription) {
      editQuestionnaire({
        description: newDescription,
      } as Partial<Questionnaire>);
    } else {
      setNewDescription(questionnaire.description);
    }
  };

  if (open && loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth={true}>
      <DialogTitle>
        {/* Dialog Title and Edit Description */}
        <Box sx={{ pt: 1 }}>
          <Grid2 container spacing={1}>
            <Grid2 display="flex" justifyContent="left" alignItems="center">
              <EditText
                text={questionnaire.title}
                editText={setTitle}
                label={'Title'}
              />
            </Grid2>
            <Grid2 display="flex" justifyContent="left" alignItems="center">
              <Button
                variant="contained"
                onClick={() => setDescriptionDialogOpen(true)}
              >
                Edit Description
              </Button>
            </Grid2>
          </Grid2>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <div style={{ display: 'flex' }}>
          {/* PDF Viewer */}
          <div style={{ flex: '0 0 60%' }}>
            <Paper sx={{ mb: 3, p: 2 }} style={{ border: '1px solid #ccc' }}>
              <div style={{ height: '600px' }}>
                <iframe
                  width="100%"
                  height="100%"
                  src={pdfBlobURL}
                  title={'Form PDF'}
                />
              </div>
            </Paper>
          </div>

          {/* Questions column */}
          <div style={{ flex: '1' }}>
            <Paper sx={{ mb: 3, p: 2 }} style={{ border: '1px solid #ccc' }}>
              <div style={{ height: '600px', overflow: 'auto' }}>
                {questionnaire.questions.map((q) => {
                  return (
                    <div>
                      <Question
                        question={q}
                        editQuestion={editQuestion}
                        pdfFields={fieldNames}
                        deleteQuestion={deleteQuestion}
                        swapQuestions={swapQuestions}
                      />
                    </div>
                  );
                })}

                <Button variant="contained" fullWidth onClick={createQuestion}>
                  Add Question
                </Button>
              </div>
            </Paper>
          </div>
        </div>
      </DialogContent>

      {/* Dialog Buttons */}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          component="label"
          disabled={isUploading}
          onClick={handleUpload}
        >
          {isUploading ? 'Uploading...' : 'Upload PDF'}
          <input type="file" hidden accept=".pdf" onChange={handleFileSelect} />
        </Button>
        <Button
          variant="contained"
          onClick={saveQuestionnaire}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        {saveError && <Typography>{saveErrorText}</Typography>}
      </DialogActions>

      {/* Description editor dialog */}
      <Dialog
        open={descriptionDialogOpen}
        onClose={() => descriptionDialogClose()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" gutterBottom textAlign={'center'}>
            Form Description
          </Typography>
        </DialogTitle>
        <Box sx={{ p: 2 }}>
          <Grid2 container spacing={2} width="100%">
            <Grid2
              display="flex"
              width="100%"
              alignItems="center"
              justifyContent="left"
            >
              <FormControl fullWidth>
                <TextField
                  id="outlined-controlled"
                  label="Form Description"
                  value={newDescription}
                  multiline
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setNewDescription(event.target.value);
                  }}
                />
              </FormControl>
            </Grid2>
            <Grid2
              display="flex"
              width="100%"
              alignItems="bottom"
              justifyContent="center"
            >
              <FormControl>
                <Button
                  variant="contained"
                  onClick={() => {
                    descriptionDialogClose(newDescription);
                  }}
                >
                  Save
                </Button>
              </FormControl>
            </Grid2>
          </Grid2>
        </Box>
      </Dialog>
    </Dialog>
  );
};

// Title edit component
interface EditTextProps {
  text: string;
  editText: (text: string) => void;
  label: string;
}

const EditText: React.FC<EditTextProps> = ({ text, editText, label }) => {
  const [state, setState] = useState<boolean>(false);
  switch (state) {
    case true:
      return (
        <Box>
          <Grid2 container spacing={2}>
            <Grid2 display="flex" alignItems="center">
              <TextField
                id="outlined-controlled"
                label={label}
                value={text}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  editText(event.target.value);
                }}
              />
            </Grid2>
            <Grid2 display="flex" justifyContent="left" alignItems="center">
              <Button variant="contained" onClick={() => setState(false)}>
                Done
              </Button>
            </Grid2>
          </Grid2>
        </Box>
      );
    case false:
      return (
        <Box>
          <Grid2 container spacing={2}>
            <Grid2 display="flex" alignItems="center">
              <Typography variant="h5" gutterBottom textAlign={'center'}>
                {text}
              </Typography>
            </Grid2>
            <Grid2 display="flex" justifyContent="left" alignItems="center">
              <Button variant="contained" onClick={() => setState(true)}>
                Edit
              </Button>
            </Grid2>
          </Grid2>
        </Box>
      );
    default:
      return null;
  }
};

interface QuestionDataProps {
  question: QuestionnaireQuestion;
  editQuestion: (index: number, edit: Partial<QuestionnaireQuestion>) => void;
  pdfFields: PDFField[];
}

interface QuestionProps {
  question: QuestionnaireQuestion;
  editQuestion: (index: number, edit: Partial<QuestionnaireQuestion>) => void;
  deleteQuestion: (index: number) => void;
  swapQuestions: (index1: number, index2: number) => void;
  pdfFields: PDFField[];
}

const Question: React.FC<QuestionProps> = ({
  question,
  editQuestion,
  pdfFields,
  deleteQuestion,
  swapQuestions,
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Render the content based on the question type
  const renderContent = () => {
    switch (question.type) {
      case 'text':
        return (
          <EditTextQuestion
            question={question}
            editQuestion={editQuestion}
            pdfFields={pdfFields}
          />
        );
      case 'radiogroup':
        return (
          <RadioQuestionTable
            question={question}
            editQuestion={editQuestion}
            pdfFields={pdfFields}
          />
        );
      case 'checkboxes':
        return (
          <CheckBoxesTable
            question={question}
            editQuestion={editQuestion}
            pdfFields={pdfFields}
          />
        );
      case 'date':
        return (
          <DateQuestionTable
            question={question}
            editQuestion={editQuestion}
            pdfFields={pdfFields}
          />
        );
      default:
        return null;
    }
  };

  const changeQuestionType = (event: SelectChangeEvent) => {
    var edits = {};
    switch (event.target.value) {
      case 'text':
        edits = {
          type: 'text',
          questionData: {
            answers: [
              {
                label: '',
                defaultValue: '',
                pdfMap: '',
              } as TextQuestionAnswer,
            ],
          } as TextQuestion,
        } as Partial<QuestionnaireQuestion>;
        editQuestion(question.index, edits);
        break;

      case 'radiogroup':
        edits = {
          type: 'radiogroup',
          questionData: {
            defaultValue: 0,
            pdfRadioGroup: '',
            answers: [
              { index: 0, text: '', pdfRadioMap: '' } as RadioQuestionAnswer,
            ],
          } as RadioQuestion,
        } as Partial<QuestionnaireQuestion>;
        editQuestion(question.index, edits);
        break;

      case 'checkboxes':
        edits = {
          type: 'checkboxes',
          questionData: {
            defaultValue: 0,
            answers: [
              {
                index: 0,
                text: '',
                pdfMap: '',
                defaultValue: false,
              } as CheckBoxesAnswer,
            ],
          } as CheckBoxes,
        } as Partial<QuestionnaireQuestion>;
        editQuestion(question.index, edits);
        break;

      case 'date':
        edits = {
          type: 'date',
          questionData: {
            text: '',
            pdfMap: '',
            startDate: '2025-01-01',
            endDate: '2099-12-31',
          } as DateQuestion,
        } as Partial<QuestionnaireQuestion>;
        editQuestion(question.index, edits);
        break;
      default:
        return null;
    }
  };

  const setQuestionText = (text: string) => {
    editQuestion(question.index, {
      questionText: text,
    } as QuestionnaireQuestion);
  };

  return (
    <div>
      <Accordion
        key={question.index}
        elevation={1}
        sx={{ mb: 1, p: 2 }}
        style={{ border: '1px solid #ccc' }}
        defaultExpanded
      >
        <AccordionSummary
          expandIcon={<ArrowBigUp />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography variant="h5" gutterBottom textAlign={'center'}>
            Question {question.index + 1}
          </Typography>
        </AccordionSummary>
        <Box sx={{ mb: 1, p: 2, margin: 'auto' }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <IconButton
              onClick={() => swapQuestions(question.index, question.index - 1)}
            >
              <ArrowBigUp size={22} />
            </IconButton>
            <IconButton
              onClick={() => swapQuestions(question.index, question.index + 1)}
            >
              <ArrowBigDown size={22} />
            </IconButton>
            <IconButton onClick={() => setConfirmDialogOpen(true)}>
              <Trash2 size={22} />
            </IconButton>
          </Box>
        </Box>

        <Table component={Paper} sx={{ mb: 1, p: 2 }}>
          <TableHead>
            <TableCell>Question</TableCell>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <TextField
                  fullWidth={true}
                  id="outlined-controlled"
                  label="Question Text"
                  multiline={true}
                  value={question.questionText}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setQuestionText(event.target.value);
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">
                    Question Type
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Question Type"
                    value={question.type}
                    onChange={changeQuestionType}
                  >
                    <MenuItem value={'text'}>Text</MenuItem>
                    <MenuItem value={'radiogroup'}>Radio Group</MenuItem>
                    <MenuItem value={'checkboxes'}>Check Boxes</MenuItem>
                    <MenuItem value={'date'}>Date</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box sx={{ mb: 1, pt: 2 }}>{renderContent()}</Box>
      </Accordion>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this question
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmDialogOpen(false);
              deleteQuestion(question.index);
            }}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmDialogOpen(false);
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const EditTextQuestion: React.FC<QuestionDataProps> = ({
  question,
  editQuestion,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pdfFields,
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const editQuestionData = (edits: Partial<TextQuestion>) => {
    const newQuestionData = {
      ...question.questionData,
      ...edits,
    } as TextQuestion;

    editQuestion(question.index, {
      questionData: newQuestionData,
    } as Partial<QuestionnaireQuestion>);
  };

  const setLabel = (index: number, newValue: string) => {
    var newAnswers = [...(question.questionData as TextQuestion).answers];

    newAnswers[index] = {
      ...newAnswers[index],
      label: newValue,
    } as TextQuestionAnswer;

    const edits = {
      answers: newAnswers,
    } as Partial<TextQuestion>;
    editQuestionData(edits);
  };

  const setDefaultValue = (index: number, newValue: string) => {
    var newAnswers = [...(question.questionData as TextQuestion).answers];

    newAnswers[index] = {
      ...newAnswers[index],
      defaultValue: newValue,
    } as TextQuestionAnswer;

    const edits = {
      answers: newAnswers,
    } as Partial<TextQuestion>;
    editQuestionData(edits);
  };

  const setpdfMap = (index: number, map: string) => {
    if (map !== '') {
      var newAnswers = [...(question.questionData as TextQuestion).answers];

      newAnswers[index] = {
        ...newAnswers[index],
        pdfMap: map,
      } as TextQuestionAnswer;

      const edits = {
        answers: newAnswers,
      } as Partial<TextQuestion>;
      editQuestionData(edits);
    }
  };

  const addOption = () => {
    var newAnswers = [...(question.questionData as TextQuestion).answers];

    newAnswers.push({
      defaultValue: '',
      pdfMap: '',
    } as TextQuestionAnswer);

    const edits = {
      answers: newAnswers,
    } as Partial<TextQuestion>;

    editQuestionData(edits);
  };

  const deleteOption = (index: number) => {
    setAnchorEl(null);

    var newAnswers = [...(question.questionData as TextQuestion).answers];

    newAnswers.splice(index, 1);

    const edits = {
      answers: newAnswers,
    } as Partial<TextQuestion>;

    editQuestionData(edits);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedAnswer(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <Table component={Paper} sx={{ mb: 1, p: 2 }}>
        <TableHead>
          <TableCell colSpan={3}>Fields</TableCell>
        </TableHead>
        <TableBody>
          {(question.questionData as TextQuestion).answers.map(
            (answer, index) => (
              <TextAnswerRow
                answer={answer}
                index={index}
                handleMenuOpen={handleMenuOpen}
                setDefaultValue={setDefaultValue}
                setpdfMap={setpdfMap}
                pdfFields={pdfFields}
                setLabel={setLabel}
              />
            )
          )}
        </TableBody>
      </Table>
      <Button variant="contained" fullWidth={true} onClick={addOption}>
        Add Option
      </Button>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            deleteOption(selectedAnswer);
          }}
        >
          <Trash2 size={18} style={{ marginRight: 8 }} />
          Delete Answer
        </MenuItem>
      </Menu>
    </Box>
  );
};

interface TextAnswerRowProps {
  answer: TextQuestionAnswer;
  index: number;
  handleMenuOpen: (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => void;
  setDefaultValue: (index: number, newValue: string) => void;
  setpdfMap: (index: number, map: string) => void;
  setLabel: (index: number, label: string) => void;
  pdfFields: PDFField[];
}

const TextAnswerRow: React.FC<TextAnswerRowProps> = ({
  answer,
  index,
  handleMenuOpen,
  setDefaultValue,
  setpdfMap,
  setLabel,
  pdfFields,
}) => {
  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell colSpan={2}>
          <TextField
            id="outlined-controlled"
            label="Field Label"
            multiline={true}
            fullWidth={true}
            value={answer.label}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setLabel(index, event.target.value);
            }}
          />
        </TableCell>
        <TableCell align="right">
          <IconButton onClick={(e) => handleMenuOpen(e, index)}>
            <MoreVertical size={18} />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow style={{ paddingBottom: 0, paddingTop: 0 }}>
        <TableCell colSpan={2}>
          <TextField
            id="outlined-controlled"
            label="Default Text"
            multiline={true}
            fullWidth={true}
            value={answer.defaultValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setDefaultValue(index, event.target.value);
            }}
          />
        </TableCell>
        <TableCell>
          <FormControl style={{ width: 150 }}>
            <InputLabel id="demo-simple-select-label">PDF Mapping</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="PDF Mapping"
              type="string"
              error={answer.pdfMap === ''}
              value={answer.pdfMap}
              onChange={(event) => setpdfMap(index, event.target.value)}
            >
              {pdfFields &&
                pdfFields.map(
                  (field) =>
                    field instanceof PDFTextField && (
                      <MenuItem value={field.getName()}>
                        {field.getName()}
                      </MenuItem>
                    )
                )}
            </Select>
          </FormControl>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const CheckBoxesTable: React.FC<QuestionDataProps> = ({
  question,
  editQuestion,
  pdfFields,
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const editQuestionData = (edits: Partial<CheckBoxes>) => {
    const newQuestionData = {
      ...question.questionData,
      ...edits,
    } as CheckBoxes;

    editQuestion(question.index, {
      questionData: newQuestionData,
    } as Partial<QuestionnaireQuestion>);
  };

  const setAnswer = (index: number, edit: string) => {
    var newAnswers = [...(question.questionData as CheckBoxes).answers];

    newAnswers[index] = {
      ...newAnswers[index],
      text: edit,
    } as CheckBoxesAnswer;

    const edits = {
      answers: newAnswers,
    } as Partial<CheckBoxes>;
    editQuestionData(edits);
  };

  const setDefaultValue = (index: number, newValue: boolean) => {
    var newAnswers = [...(question.questionData as CheckBoxes).answers];

    newAnswers[index] = {
      ...newAnswers[index],
      defaultValue: newValue,
    } as CheckBoxesAnswer;

    const edits = {
      answers: newAnswers,
    } as Partial<CheckBoxes>;
    editQuestionData(edits);
  };

  const getDefaultValue = (index: number) => {
    return (question.questionData as CheckBoxes).answers[index].defaultValue;
  };

  const getDefaultValueText = (index: number) => {
    if (getDefaultValue(index)) {
      return 'Set Default to False';
    } else {
      return 'Set Default to True';
    }
  };

  const setpdfMap = (index: number, map: string) => {
    var newAnswers = [...(question.questionData as CheckBoxes).answers];

    newAnswers[index] = {
      ...newAnswers[index],
      pdfMap: map,
    } as CheckBoxesAnswer;

    const edits = {
      answers: newAnswers,
    } as Partial<CheckBoxes>;
    editQuestionData(edits);
  };

  const addOption = () => {
    var newAnswers = [...(question.questionData as CheckBoxes).answers];

    newAnswers.push({
      index: newAnswers.length,
      text: '',
      pdfMap: '',
      defaultValue: false,
    } as CheckBoxesAnswer);

    const edits = {
      answers: newAnswers,
    } as Partial<CheckBoxes>;

    editQuestionData(edits);
  };

  const deleteOption = (index: number) => {
    setAnchorEl(null);

    var newAnswers = [...(question.questionData as CheckBoxes).answers];

    newAnswers.splice(index, 1);
    newAnswers.map((answer, index) => (answer.index = index));

    const edits = {
      answers: newAnswers,
    } as Partial<CheckBoxes>;

    editQuestionData(edits);
  };

  const buttonIcon = (index: number) => {
    if (getDefaultValue(index)) {
      return <Star size={18} />;
    } else {
      return <MoreVertical size={18} />;
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedAnswer(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Answers</TableCell>
              <TableCell>PDF Mapping</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(question.questionData as CheckBoxes).answers.map(
              (answer) =>
                answer && (
                  <TableRow>
                    <TableCell>
                      <TextField
                        id="outlined-controlled"
                        label="Answer"
                        multiline={true}
                        value={answer.text}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setAnswer(answer.index, event.target.value);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl style={{ width: 150 }}>
                        <InputLabel id="demo-simple-select-label">
                          PDF Mapping
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label="PDF Mapping"
                          type="string"
                          fullWidth={false}
                          error={answer.pdfMap === ''}
                          value={answer.pdfMap}
                          onChange={(event) =>
                            setpdfMap(answer.index, event.target.value)
                          }
                        >
                          {pdfFields &&
                            pdfFields.map(
                              (field) =>
                                field instanceof PDFCheckBox && (
                                  <MenuItem value={field.getName()}>
                                    {field.getName()}
                                  </MenuItem>
                                )
                            )}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, answer.index)}
                      >
                        {buttonIcon(answer.index)}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
            )}
          </TableBody>
        </Table>
        <Button variant="contained" fullWidth={true} onClick={addOption}>
          Add Option
        </Button>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() =>
            setDefaultValue(selectedAnswer, !getDefaultValue(selectedAnswer))
          }
        >
          <Star size={18} style={{ marginRight: 8 }} />
          {getDefaultValueText(selectedAnswer)}
        </MenuItem>
        <MenuItem
          onClick={() => {
            deleteOption(selectedAnswer);
          }}
        >
          <Trash2 size={18} style={{ marginRight: 8 }} />
          Delete Answer
        </MenuItem>
      </Menu>
    </Box>
  );
};

const RadioQuestionTable: React.FC<QuestionDataProps> = ({
  question,
  editQuestion,
  pdfFields,
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const editQuestionData = (edits: Partial<RadioQuestion>) => {
    const newQuestionData = {
      ...question.questionData,
      ...edits,
    } as RadioQuestion;

    editQuestion(question.index, {
      questionData: newQuestionData,
    } as Partial<QuestionnaireQuestion>);
  };

  const setAnswer = (index: number, edit: string) => {
    var newAnswers = [...(question.questionData as RadioQuestion).answers];

    newAnswers[index] = {
      ...newAnswers[index],
      text: edit,
    } as RadioQuestionAnswer;

    const edits = {
      answers: newAnswers,
    } as Partial<RadioQuestion>;
    editQuestionData(edits);
  };

  const setDefaultValue = (newValue: number) => {
    const edits = {
      defaultValue: newValue,
    } as Partial<RadioQuestion>;
    editQuestionData(edits);
  };

  const setpdfRadioGroup = (group: string) => {
    editQuestionData({ pdfRadioGroup: group } as Partial<RadioQuestion>);
  };

  const setpdfRadioMap = (index: number, map: string) => {
    var newAnswers = [...(question.questionData as RadioQuestion).answers];

    newAnswers[index] = {
      ...newAnswers[index],
      pdfRadioMap: map,
    } as RadioQuestionAnswer;

    const edits = {
      answers: newAnswers,
    } as Partial<RadioQuestion>;
    editQuestionData(edits);
  };

  const addOption = () => {
    var newAnswers = [...(question.questionData as RadioQuestion).answers];

    newAnswers.push({
      index: newAnswers.length,
      text: '',
      pdfRadioMap: '',
    } as RadioQuestionAnswer);

    const edits = {
      answers: newAnswers,
    } as Partial<RadioQuestion>;

    editQuestionData(edits);
  };

  const deleteOption = (index: number) => {
    setAnchorEl(null);
    var val = (question.questionData as RadioQuestion).defaultValue;
    if (index <= val) {
      val -= 1;
    }

    var newAnswers = [...(question.questionData as RadioQuestion).answers];

    newAnswers.splice(index, 1);
    newAnswers.map((answer, index) => (answer.index = index));

    const edits = {
      answers: newAnswers,
      defaultValue: val,
    } as Partial<RadioQuestion>;

    editQuestionData(edits);
  };

  // if the answer is the default value, show a star icon, otherwise show a more vertical icon
  const buttonIcon = (index: number) => {
    if (index === (question.questionData as RadioQuestion).defaultValue) {
      return <Star size={18} />;
    } else {
      return <MoreVertical size={18} />;
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedAnswer(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <Table component={Paper} sx={{ mb: 1, pt: 2 }}>
        <TableHead>
          <TableCell>PDF Radio Group</TableCell>
        </TableHead>
        <TableBody>
          <Typography textAlign={'center'} sx={{ mb: 1, p: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">PDF Mapping</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="PDF Radio Group"
                type="string"
                error={
                  (question.questionData as RadioQuestion).pdfRadioGroup === ''
                }
                fullWidth={false}
                value={(question.questionData as RadioQuestion).pdfRadioGroup}
                onChange={(event) => setpdfRadioGroup(event.target.value)}
              >
                {pdfFields &&
                  pdfFields.map(
                    (field) =>
                      field instanceof PDFRadioGroup && (
                        <MenuItem value={field.getName()}>
                          {field.getName()}
                        </MenuItem>
                      )
                  )}
              </Select>
            </FormControl>
          </Typography>
        </TableBody>
      </Table>
      <TableContainer component={Paper} sx={{ mb: 1, pt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Answers</TableCell>
              <TableCell>PDF Mapping</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(question.questionData as RadioQuestion).answers.map(
              (answer) =>
                answer && (
                  <TableRow>
                    <TableCell>
                      <TextField
                        id="outlined-controlled"
                        label="Answer"
                        multiline={true}
                        value={answer.text}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setAnswer(answer.index, event.target.value);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl style={{ width: 150 }}>
                        <InputLabel id="demo-simple-select-label">
                          Radio Option Map
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label="Option Map"
                          type="string"
                          error={answer.pdfRadioMap === ''}
                          fullWidth={false}
                          value={answer.pdfRadioMap}
                          onChange={(event) =>
                            setpdfRadioMap(answer.index, event.target.value)
                          }
                        >
                          {pdfFields &&
                            pdfFields.map(
                              (field) =>
                                field instanceof PDFRadioGroup &&
                                field.getName() ===
                                  (question.questionData as RadioQuestion)
                                    .pdfRadioGroup &&
                                field
                                  .getOptions()
                                  .map((option) => (
                                    <MenuItem value={option}>{option}</MenuItem>
                                  ))
                            )}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, answer.index)}
                      >
                        {buttonIcon(answer.index)}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
            )}
          </TableBody>
        </Table>
        <Button variant="contained" fullWidth={true} onClick={addOption}>
          Add Option
        </Button>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => setDefaultValue(selectedAnswer)}>
          <Star size={18} style={{ marginRight: 8 }} />
          Set as Default
        </MenuItem>
        <MenuItem
          onClick={() => {
            deleteOption(selectedAnswer);
          }}
        >
          <Trash2 size={18} style={{ marginRight: 8 }} />
          Delete Answer
        </MenuItem>
      </Menu>
    </Box>
  );
};

const DateQuestionTable: React.FC<QuestionDataProps> = ({
  question,
  editQuestion,
  pdfFields,
}) => {
  const editQuestionData = (edits: Partial<DateQuestion>) => {
    const newQuestionData = {
      ...question.questionData,
      ...edits,
    } as DateQuestion;

    editQuestion(question.index, {
      questionData: newQuestionData,
    } as Partial<QuestionnaireQuestion>);
  };

  const setpdfMap = (map: string) => {
    const edits = {
      pdfMap: map,
    } as Partial<DateQuestion>;
    editQuestionData(edits);
  };

  const setStartDate = (date: string) => {
    const edits = {
      startDate: date,
    } as Partial<DateQuestion>;
    editQuestionData(edits);
  };

  const setEndDate = (date: string) => {
    const edits = {
      startDate: date,
    } as Partial<DateQuestion>;
    editQuestionData(edits);
  };

  return (
    <Box>
      <Table component={Paper} sx={{ mb: 1, pt: 2 }}>
        <TableHead>
          <TableCell>Date Question</TableCell>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">
                  PDF Mapping
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  label="PDF Mapping"
                  type="string"
                  error={(question.questionData as DateQuestion).pdfMap === ''}
                  value={(question.questionData as DateQuestion).pdfMap}
                  onChange={(event) => setpdfMap(event.target.value)}
                >
                  {pdfFields &&
                    pdfFields.map(
                      (field) =>
                        field instanceof PDFTextField && (
                          <MenuItem value={field.getName()}>
                            {field.getName()}
                          </MenuItem>
                        )
                    )}
                </Select>
              </FormControl>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControl fullWidth>
                <TextField
                  label="Valid Date Interval Start"
                  type="date"
                  value={(question.questionData as DateQuestion).startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </FormControl>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControl fullWidth>
                <TextField
                  label="Valid Date Interval End"
                  type="date"
                  value={(question.questionData as DateQuestion).endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </FormControl>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

export default FormEditorDialog;
